import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

import { AdonisIdentity } from "../../adonis.types";
import { ToolStatusLine } from "../ToolStatusLine";

import { AdonisCtaButton, AdonisCtaIcon } from "./AdonisCtaButton";
import type { AdonisProductRecommendationCardProps } from "./AdonisProductRecommendationCard";
import { AdonisProductRecommendationsCarousel } from "./AdonisProductRecommendationsCarousel";

export interface AdonisToolRendererProps {
  toolName: string;
  toolId: string;
  status: "pending" | "success" | "failed";
  persona: AdonisIdentity;
  input?: Record<string, unknown> | undefined;
  LinkComponent?: (props: {
    to: string;
    children: React.ReactNode;
    className?: string;
  }) => JSX.Element;
  animateText?: boolean;
}

// Define a stable default Link component at module scope to avoid re-mounts
const DefaultLinkComponent = ({
  to,
  children,
  className,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) => (
  <Link to={to} className={className} onClick={onClick}>
    {children}
  </Link>
);

/**
 * Centralized renderer for Adonis tool calls.
 * When the tool name matches a known UI-producing tool, render structured UI.
 * Otherwise, fall back to a standard ToolStatusLine.
 */
export const AdonisToolRenderer = ({
  toolName,
  toolId,
  status,
  persona,
  input,
  LinkComponent = DefaultLinkComponent,
  animateText = false,
}: AdonisToolRendererProps) => {
  // Prevent re-running card animations on every upstream re-render
  const hasAnimatedRef = useRef(false);
  const effectiveAnimateText = animateText && !hasAnimatedRef.current;
  useEffect(() => {
    if (animateText && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
    }
  }, [animateText]);

  const cards = useMemo(() => {
    return toolName === "render_product_recommendations"
      ? normalizeProductRecommendations(input) ?? []
      : [];
  }, [toolName, input]);

  // Guard: if pending or failed, show status line only
  if (status !== "success") {
    return (
      <ToolStatusLine
        toolName={`${persona.name ?? "Adonis"} ${
          status === "pending" ? "is preparing" : "encountered an issue"
        }`}
        toolId={toolId}
        status={status}
        enableAnimation={true}
      />
    );
  }

  if (toolName === "render_product_recommendations") {
    if (!cards || cards.length === 0) {
      return (
        <ToolStatusLine
          toolName="No product recommendations provided"
          toolId={toolId}
          status={"success"}
          enableAnimation={false}
        />
      );
    }

    const carousel = (
      <AdonisProductRecommendationsCarousel
        items={cards}
        LinkComponent={LinkComponent}
        animateText={effectiveAnimateText}
      />
    );

    // Always mark as full-bleed so AssistantMessage renders outside bubble
    return <div data-adonis-full-bleed>{carousel}</div>;
  }

  // Render CTA buttons (plural) inside assistant bubble
  if (toolName === "render_cta_buttons") {
    const buttons = normalizeCtaButtons(input);
    if (!buttons || buttons.length === 0) {
      return (
        <ToolStatusLine
          toolName="No CTA buttons provided"
          toolId={toolId}
          status={"success"}
          enableAnimation={false}
        />
      );
    }
    return (
      <div className="mt-2 flex flex-col gap-2">
        {buttons.map((btn, idx) => (
          <AdonisCtaButton
            key={`${toolId}-${idx}`}
            label={btn.label}
            icon={btn.icon}
            href={btn.href}
          />
        ))}
      </div>
    );
  }

  // Backward compatibility: single CTA button
  if (toolName === "render_cta_button") {
    const single = normalizeCtaInput(input);
    if (!single || !single.label) {
      return (
        <ToolStatusLine
          toolName="No CTA label provided"
          toolId={toolId}
          status={"success"}
          enableAnimation={false}
        />
      );
    }
    return (
      <div className="mt-2">
        <AdonisCtaButton
          label={single.label}
          icon={single.icon}
          href={single.href}
        />
      </div>
    );
  }

  // Default fallback: show a status line
  return (
    <ToolStatusLine
      toolName={`${persona.name ?? "Adonis"} ${toolName} got results`}
      toolId={toolId}
      status={status}
      enableAnimation={true}
    />
  );
};

function normalizeProductRecommendations(
  input?: Record<string, unknown>
): AdonisProductRecommendationCardProps[] | null {
  if (!input) return null;

  // Accept either single item or array of items. Keys use a permissive schema.
  const maybeArray = Array.isArray(input)
    ? input
    : (input.items as unknown) ?? [input];

  const asArray = Array.isArray(maybeArray) ? maybeArray : [maybeArray];

  const cards: AdonisProductRecommendationCardProps[] = [];
  for (const raw of asArray) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;

    const imageUrl = stringOrEmpty(obj.imageUrl ?? obj.image ?? obj.image_src);
    const title = stringOrEmpty(obj.title ?? obj.name);
    // Allow empty/missing imageUrl (will render placeholder); require title only
    if (!title) continue;

    // Removed as of 16 oct 2025, keeping in here just in case we add it back in the next week or so
    // rmeove if reading this in 2045 :)
    // const description = stringOrNull(
    //   obj.description ?? obj.subtitle ?? obj.short_description
    // );
    const note = stringOrNull(obj.note ?? obj.rationale ?? obj.comment);
    const priceCents = numberOrNull(obj.priceCents ?? obj.price_cents);
    const memberPriceCents = numberOrNull(
      obj.memberPriceCents ?? obj.member_price_cents
    );
    const href = stringOrNull(obj.href);

    cards.push({
      imageUrl,
      title,
      // description, // removed as of 16 oct 2025
      note,
      priceCents,
      memberPriceCents,
      href,
    });
  }

  return cards;
}

// cleaned up: no longer need flag helpers

const stringOrNull = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

const stringOrEmpty = (v: unknown): string => (typeof v === "string" ? v : "");

const numberOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

export default React.memo(AdonisToolRenderer);

function normalizeCtaInput(input?: Record<string, unknown>): {
  label: string;
  icon?: AdonisCtaIcon;
  href?: string;
} | null {
  if (!input || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;
  const label = typeof obj.label === "string" ? obj.label : "";
  if (!label) return null;
  const iconRaw = typeof obj.icon === "string" ? obj.icon : undefined;
  const href = typeof obj.href === "string" ? obj.href : undefined;
  const icon = mapIcon(iconRaw);
  return { label, icon, href };
}

type NormalizedCta = { label: string; icon?: AdonisCtaIcon; href?: string };

function normalizeCtaButtons(input?: Record<string, unknown>): NormalizedCta[] {
  if (!input) return [];
  const maybeArray = Array.isArray(input)
    ? (input as unknown[])
    : Array.isArray((input as Record<string, unknown>).items)
    ? ((input as Record<string, unknown>).items as unknown[]) ?? []
    : [input];

  const arr = Array.isArray(maybeArray) ? maybeArray : [maybeArray];
  const results: NormalizedCta[] = [];
  for (const raw of arr) {
    if (!raw || typeof raw !== "object") continue;
    const normalized = normalizeCtaInput(raw as Record<string, unknown>);
    if (normalized) results.push(normalized);
  }
  return results;
}

function mapIcon(value?: string): AdonisCtaIcon | undefined {
  if (!value) return undefined;
  const v = value.toLowerCase();
  if (v === "calendar") return "calendar";
  if (v === "phone") return "phone";
  if (v === "link") return "link";
  if (v === "info") return "info";
  return undefined;
}
